import { useState, useCallback, useEffect, ChangeEvent, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import { useSetAtom } from "jotai";
import { CustormerParams } from "@/app/services/customerService/customer";
import { HandleCustomerStatusParams } from "@/app/services/customerService/handleCustomerStatus";
import { useCustomerMutations } from "@/app/hooks/useCustomerMutations";
import { Search, Calendar, Wallet, AArrowDown } from "lucide-react";
import { EmptyContent } from "@/view/components/EmptyContent";
import { useDebounce } from "@/app/hooks/useDebounce";
import { useCustomers } from "@/app/hooks/useCustomers";
import { useAuth } from "@/app/hooks/useAuth";
import { PartnerType } from "@/app/utils/partners.enum";
import { buildWhatsappUrl } from "@/app/utils/whatsapp";
import { crefazProposalDataAtom } from "@/app/store";
import { useQuery } from "@tanstack/react-query";
import { ProductCatalogItem } from "@/app/services/configService/getProductsCatalog";
import { configService } from "@/app/services/configService";

enum TabType {
  WithBalance = "withBalance",
  Unauthorized = "unauthorized",
  Archived = "archived",
}

export const useManageCustomerController = () => {
  const { user } = useAuth();
  const { updateCustomerStatus } = useCustomerMutations();
  const navigate = useNavigate();
  const setCrefazProposalData = useSetAtom(crefazProposalDataAtom);
  const [activeTab, setActiveTab] = useState<TabType>(TabType.WithBalance);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(500, searchValue);
  const { ref, inView } = useInView();
  const [contactedCustomers, setContactedCustomers] = useState<Set<string>>(
    new Set(),
  );
  const [sort, setSort] = useState<string>("available.updatedAt");
  const [order, setOrder] = useState<1 | -1>(-1);

  const tabs = useMemo(
    () => [
      {
        id: TabType.WithBalance,
        label: "Com saldo",
      },
      {
        id: TabType.Unauthorized,
        label: "Não autorizado",
      },
      {
        id: TabType.Archived,
        label: "Arquivados",
      },
    ],
    [],
  );

  const handleCustomerDetailsPage = (id: string) => {
    window.location.assign(`/clientes/${id}`);
  };

  const { data: productsCatalog } = useQuery<ProductCatalogItem[]>({
    queryKey: ["productsCatalog"],
    queryFn: configService.getProductsCatalog,
  });

  const {
    data,
    isFetching,
    isLoading,
    setParams,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useCustomers({ initialLimit: 30, userId: user?._id });

  const setSkip = useCallback(
    (value: number) => {
      setParams((state) => ({ ...state, skip: value }));
    },
    [setParams],
  );
  const setSearch = useCallback(() => {
    setParams((state) => ({ ...state, search: debouncedSearchValue }));
  }, [debouncedSearchValue, setParams]);

  useEffect(() => {
    setSearch();
  }, [setSearch]);

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleCustomerDetailsProposal = (cpf: string) => {
    window.location.assign(`/transacoes/${cpf}`);
  };

  const handleProposal = async ({
    id,
    cpf,
    zipCode,
    type,
  }: {
    id: string;
    cpf: string;
    zipCode?: string;
    type: PartnerType;
  }) => {
    await handleCustomerStatus({ id, action: "contact" });

    if (type === PartnerType.CREFAZ) {
      setCrefazProposalData({
        cpf,
        zipcode: zipCode,
        shouldOpenModal: true,
      });
      navigate("/transacoes");
    }

    if (type === PartnerType.FGTS) {
      handleCustomerDetailsProposal(cpf);
    }
  };

  const openWhatsApp = ({ _id: id, phone }: { _id: string; phone: string }) => {
    let text = "";
    if (activeTab === TabType.WithBalance)
      text =
        "Olá, vi que o seu saldo do FGTS já está atualizado. Gostaria de antecipar o saldo disponível?";
    else if (activeTab === TabType.Unauthorized)
      text = "Olá, gostaria de simular uma nova antecipação do FGTS?";

    const url = buildWhatsappUrl(phone, text);

    handleCustomerStatus({ id, action: "contact" });
    window.open(url, "_blank");
  };

  const handleCustomerStatus = async (input: HandleCustomerStatusParams) => {
    try {
      await updateCustomerStatus(input);
      setContactedCustomers((prev) => new Set(prev).add(input.id));
      await refetch();
    } catch (error: any) {}
  };

  useEffect(() => {
    if (data) {
      const contactedFromData = new Set(
        data.pages.flatMap((page) =>
          page.customers
            .filter((c) => c.available?.isContacted)
            .map((c) => c._id),
        ),
      );
      setContactedCustomers(contactedFromData);
    }
  }, [data]);

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  useEffect(() => {
    let availableStatus: CustormerParams["availableStatus"];

    switch (activeTab) {
      case TabType.WithBalance:
        availableStatus = "HAS_AMOUNT";
        break;
      case TabType.Unauthorized:
        availableStatus = "UNAUTHORIZED";
        break;
      case TabType.Archived:
        availableStatus = "ARCHIVED";
        break;
      default:
        availableStatus = undefined;
    }

    setParams((state) => ({
      ...state,
      availableStatus,
      skip: 0,
    }));
  }, [activeTab, setParams]);

  const handleSortChange = (newSort: string) => {
    setSort((prevSort) => {
      if (prevSort === newSort) {
        setOrder((prevOrder) => (prevOrder === 1 ? -1 : 1));
        return newSort;
      } else {
        setOrder(newSort === "name" ? 1 : -1);
        return newSort;
      }
    });
  };

  const sortIcon = useMemo(() => {
    switch (sort) {
      case "available.updatedAt":
        return <Calendar size={20} className="text-primaryHex" />;
      case "available.amount":
        return <Wallet size={20} className="text-primaryHex" />;
      case "name":
        return <AArrowDown size={20} className="text-primaryHex" />;
      default:
        return <Search size={20} className="text-primaryHex" />;
    }
  }, [sort]);

  useEffect(() => {
    setParams((state) => ({
      ...state,
      sort,
      order: String(order),
      skip: 0,
    }));
  }, [sort, order, setParams]);

  const renderNoCustomers = () => {
    const noCustomers =
      data?.pages.every((page) => page.customers.length === 0) ?? false;
    if (noCustomers) {
      return <EmptyContent />;
    }
    return null;
  };

  return {
    tabs,
    activeTab,
    setActiveTab,
    TabType,
    handleCustomerDetailsPage,
    setSkip,
    data,
    isFetching,
    isLoading,
    searchValue,
    handleSearchInputChange,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    ref,
    openWhatsApp,
    handleCustomerStatus,
    contactedCustomers,
    sort,
    handleSortChange,
    sortIcon,
    renderNoCustomers,
    handleProposal,
    productsCatalog,
  };
};
